import { memo, type ReactNode } from 'react';
import { MoreHorizontal, X } from 'lucide-react';
import { Dropdown, IconButton, type DropdownItem } from '@cyphex/ui';

export interface SidePanelProps {
  title: string;
  onClose: () => void;
  /** Overflow menu items; the "..." button is hidden when empty. */
  menuItems?: readonly DropdownItem[];
  /**
   * DOM id, so the control that opened the rail can point `aria-controls` at
   * it. Optional: a rail nothing toggles has nothing to be pointed at by.
   */
  id?: string;
  children: ReactNode;
}

/**
 * The 320px right rail.
 *
 * Shared by the meeting panels (chat, people, notes, polls, Q&A, rooms) and by
 * the call's chat rail, which is why it lives in `components/common` rather
 * than inside either feature — a CALL whose chrome was owned by `features/
 * meeting` was the one awkward part of reusing it.
 */
export const SidePanel = memo(function SidePanel({
  title, onClose, menuItems, id, children,
}: SidePanelProps) {
  return (
    <aside
      id={id}
      aria-label={title}
      className="flex w-aside shrink-0 flex-col border-l border-border bg-card"
    >
      <header className="flex h-channel-header shrink-0 items-center justify-between gap-2 px-4">
        <h2 className="truncate text-lg font-semibold">{title}</h2>
        <div className="flex items-center gap-0.5">
          {menuItems && menuItems.length > 0 ? (
            <Dropdown
              items={menuItems}
              trigger={<IconButton icon={MoreHorizontal} label={`${title} options`} size="md" />}
            />
          ) : null}
          <IconButton icon={X} label={`Close ${title.toLowerCase()}`} size="md" onClick={onClose} />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </aside>
  );
});
